import apiClient from './axios';

export interface FeedPredictionResult {
  dailyFeedRequirement: number;
  weeklyFeedRequirement: number;
  monthlyFeedRequirement: number;
  totalCost: number;
  confidence: number;
  factors: string[];
  recommendations: string[];
}

export interface WaterQualityPrediction {
  temperature: number;
  ph: number;
  dissolvedOxygen: number;
  riskLevel: 'low' | 'medium' | 'high';
  recommendations: string[];
  confidence: number;
}

export interface MortalityPrediction {
  predictedMortalityRate: number;
  riskFactors: string[];
  recommendations: string[];
  confidence: number;
}

export interface HarvestPrediction {
  optimalHarvestDate: string;
  expectedWeight: number;
  expectedBiomass: number;
  confidence: number;
  factors: string[];
}

export interface ComprehensiveAnalysisRequest {
  animalGroupIds?: string[];
  pondIds?: string[];
  analysisType: 'feed' | 'water' | 'mortality' | 'harvest' | 'all';
}

export interface ComprehensiveAnalysisResult {
  timestamp: string;
  analysisType: string;
  results: {
    feedPredictions?: FeedPredictionResult[];
    waterQualityPredictions?: WaterQualityPrediction[];
    mortalityPredictions?: MortalityPrediction[];
    harvestPredictions?: HarvestPrediction[];
  };
  summary: {
    totalPredictions: number;
    successfulPredictions: number;
    failedPredictions: number;
  };
}

// Advanced Predictive Analytics Interfaces

export interface TimeSeriesData {
  timestamp: string;
  value: number;
}

export interface PredictionModel {
  name: string;
  version: string;
  accuracy: number;
  lastTrained: string;
  parameters: Record<string, any>;
}

export interface ForecastingResult {
  predictions: Array<{
    timestamp: string;
    value: number;
    confidence: number;
  }>;
  model: PredictionModel;
  metrics: {
    mse: number;
    mae: number;
    rmse: number;
    r2: number;
  };
}

export interface AnomalyDetectionResult {
  isAnomaly: boolean;
  confidence: number;
  severity: 'low' | 'medium' | 'high';
  explanation: string;
  expectedValue: number;
  actualValue: number;
}

export interface AdvancedGrowthForecastingResult {
  forecast: ForecastingResult;
  anomalies: AnomalyDetectionResult[];
  insights: string[];
  recommendations: string[];
}

export interface AdvancedWaterQualityResult {
  temperatureForecast: ForecastingResult;
  phForecast: ForecastingResult;
  dissolvedOxygenForecast: ForecastingResult;
  anomalies: AnomalyDetectionResult[];
  riskAssessment: {
    overallRisk: 'low' | 'medium' | 'high' | 'critical';
    riskScore: number;
    riskFactors: string[];
  };
  recommendations: string[];
}

export interface PredictiveMaintenanceResult {
  equipmentHealth: {
    pumps: { status: 'good' | 'warning' | 'critical'; nextMaintenance: string; confidence: number };
    aerators: { status: 'good' | 'warning' | 'critical'; nextMaintenance: string; confidence: number };
    filters: { status: 'good' | 'warning' | 'critical'; nextMaintenance: string; confidence: number };
  };
  waterQualityTrends: {
    trend: 'improving' | 'stable' | 'declining';
    factors: string[];
  };
  recommendations: string[];
}

export interface MarketPricePredictionResult {
  predictedPrice: number;
  priceRange: { min: number; max: number };
  confidence: number;
  factors: string[];
  marketTrends: string[];
}

// Feed Requirements Prediction
export const predictFeedRequirements = async (
  animalGroupId: string,
  daysAhead: number = 30
): Promise<FeedPredictionResult> => {
  const response = await apiClient.get(`/predictive/feed-requirements/${animalGroupId}`, {
    params: { daysAhead }
  });
  return response.data;
};

// Water Quality Prediction
export const predictWaterQuality = async (
  pondId: string,
  daysAhead: number = 7
): Promise<WaterQualityPrediction> => {
  const response = await apiClient.get(`/predictive/water-quality/${pondId}`, {
    params: { daysAhead }
  });
  return response.data;
};

// Mortality Risk Prediction
export const predictMortalityRisk = async (
  animalGroupId: string
): Promise<MortalityPrediction> => {
  const response = await apiClient.get(`/predictive/mortality-risk/${animalGroupId}`);
  return response.data;
};

// Optimal Harvest Prediction
export const predictOptimalHarvest = async (
  animalGroupId: string
): Promise<HarvestPrediction> => {
  const response = await apiClient.get(`/predictive/optimal-harvest/${animalGroupId}`);
  return response.data;
};

// Comprehensive Analysis
export const runComprehensiveAnalysis = async (
  request: ComprehensiveAnalysisRequest
): Promise<ComprehensiveAnalysisResult> => {
  const response = await apiClient.post('/predictive/comprehensive-analysis', request);
  return response.data;
};

// Batch Predictions
export const batchPredictFeedRequirements = async (
  animalGroupIds: string[],
  daysAhead: number = 30
): Promise<FeedPredictionResult[]> => {
  const promises = animalGroupIds.map(id => 
    predictFeedRequirements(id, daysAhead).catch(error => ({
      animalGroupId: id,
      error: error.message,
      dailyFeedRequirement: 0,
      weeklyFeedRequirement: 0,
      monthlyFeedRequirement: 0,
      totalCost: 0,
      confidence: 0,
      factors: [],
      recommendations: []
    }))
  );
  
  return Promise.all(promises);
};

export const batchPredictWaterQuality = async (
  pondIds: string[],
  daysAhead: number = 7
): Promise<WaterQualityPrediction[]> => {
  const promises = pondIds.map(id => 
    predictWaterQuality(id, daysAhead).catch(error => ({
      pondId: id,
      error: error.message,
      temperature: 0,
      ph: 0,
      dissolvedOxygen: 0,
      riskLevel: 'medium' as const,
      recommendations: [],
      confidence: 0
    }))
  );
  
  return Promise.all(promises);
};

export const batchPredictMortalityRisk = async (
  animalGroupIds: string[]
): Promise<MortalityPrediction[]> => {
  const promises = animalGroupIds.map(id => 
    predictMortalityRisk(id).catch(error => ({
      animalGroupId: id,
      error: error.message,
      predictedMortalityRate: 0,
      riskFactors: [],
      recommendations: [],
      confidence: 0
    }))
  );
  
  return Promise.all(promises);
};

export const batchPredictOptimalHarvest = async (
  animalGroupIds: string[]
): Promise<HarvestPrediction[]> => {
  const promises = animalGroupIds.map(id => 
    predictOptimalHarvest(id).catch(error => ({
      animalGroupId: id,
      error: error.message,
      optimalHarvestDate: new Date().toISOString(),
      expectedWeight: 0,
      expectedBiomass: 0,
      confidence: 0,
      factors: []
    }))
  );
  
  return Promise.all(promises);
};

// Utility functions for data processing
export const calculateAveragePrediction = (predictions: FeedPredictionResult[]): FeedPredictionResult => {
  const validPredictions = predictions.filter(p => !('error' in p));
  
  if (validPredictions.length === 0) {
    return {
      dailyFeedRequirement: 0,
      weeklyFeedRequirement: 0,
      monthlyFeedRequirement: 0,
      totalCost: 0,
      confidence: 0,
      factors: ['No valid predictions available'],
      recommendations: ['Check data quality and try again']
    };
  }

  const avgDaily = validPredictions.reduce((sum, p) => sum + p.dailyFeedRequirement, 0) / validPredictions.length;
  const avgWeekly = validPredictions.reduce((sum, p) => sum + p.weeklyFeedRequirement, 0) / validPredictions.length;
  const avgMonthly = validPredictions.reduce((sum, p) => sum + p.monthlyFeedRequirement, 0) / validPredictions.length;
  const avgCost = validPredictions.reduce((sum, p) => sum + p.totalCost, 0) / validPredictions.length;
  const avgConfidence = validPredictions.reduce((sum, p) => sum + p.confidence, 0) / validPredictions.length;

  return {
    dailyFeedRequirement: Math.round(avgDaily * 100) / 100,
    weeklyFeedRequirement: Math.round(avgWeekly * 100) / 100,
    monthlyFeedRequirement: Math.round(avgMonthly * 100) / 100,
    totalCost: Math.round(avgCost * 100) / 100,
    confidence: Math.round(avgConfidence * 100) / 100,
    factors: [`Average of ${validPredictions.length} predictions`],
    recommendations: ['Monitor individual predictions for detailed insights']
  };
};

export const getPredictionInsights = (predictions: any[]): string[] => {
  const insights: string[] = [];
  
  if (predictions.length === 0) {
    insights.push('No predictions available');
    return insights;
  }

  const validPredictions = predictions.filter(p => !('error' in p));
  const failedPredictions = predictions.filter(p => 'error' in p);

  if (failedPredictions.length > 0) {
    insights.push(`${failedPredictions.length} predictions failed due to insufficient data`);
  }

  if (validPredictions.length > 0) {
    insights.push(`${validPredictions.length} predictions completed successfully`);
    
    // Add specific insights based on prediction type
    if ('dailyFeedRequirement' in validPredictions[0]) {
      const totalDaily = validPredictions.reduce((sum, p) => sum + p.dailyFeedRequirement, 0);
      insights.push(`Total daily feed requirement: ${totalDaily.toFixed(2)} kg`);
    }
    
    if ('predictedMortalityRate' in validPredictions[0]) {
      const avgMortality = validPredictions.reduce((sum, p) => sum + p.predictedMortalityRate, 0) / validPredictions.length;
      insights.push(`Average predicted mortality rate: ${avgMortality.toFixed(1)}%`);
    }
  }

  return insights;
};

// Advanced Predictive Analytics API Functions

export const advancedGrowthForecasting = async (
  animalGroupId: string,
  daysAhead: number = 90
): Promise<AdvancedGrowthForecastingResult> => {
  const response = await apiClient.get(`/predictive/advanced-growth-forecasting/${animalGroupId}`, {
    params: { daysAhead }
  });
  return response.data;
};

export const advancedWaterQualityAnalysis = async (
  pondId: string,
  daysAhead: number = 30
): Promise<AdvancedWaterQualityResult> => {
  const response = await apiClient.get(`/predictive/advanced-water-quality/${pondId}`, {
    params: { daysAhead }
  });
  return response.data;
};

export const predictiveMaintenanceAnalysis = async (
  pondId: string
): Promise<PredictiveMaintenanceResult> => {
  const response = await apiClient.get(`/predictive/predictive-maintenance/${pondId}`);
  return response.data;
};

export const marketPricePrediction = async (
  species: string,
  harvestDate: string
): Promise<MarketPricePredictionResult> => {
  const response = await apiClient.get('/predictive/market-price-prediction', {
    params: { species, harvestDate }
  });
  return response.data;
}; 

 